/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import ActionsMenu from '~/components/shared/ActionsMenu'
import { useTranslation } from 'react-i18next'
import { useModal } from '~/contexts/ModalContext'
import ActivityDetailModal from '~/screens/reports/activity/ActivityDetailModal'
import ActivityESignatureCommentModal from '~/screens/reports/activity/ActivityESignatureCommentModal'

interface ActivityLog {
  id?: number
  createdAt?: string | number
  createdById?: number
  createdByName?: string
  action?: string
  itemText?: string
  itemId?: number
  type?: string
  detail?: string | object
  esignatureType?: string
  esignatureComment?: string
}

interface TableMenuProps {
  log: ActivityLog
}

const TableMenu: React.FC<TableMenuProps> = ({ log }) => {
  const { t: translate } = useTranslation()
  const { showModal } = useModal()
  const [options, setOptions] = useState<any[]>([])

  useEffect(() => {
    const menu: any[] = []

    // Add "See Details" option if log has detail and action is not "Documents Migrated"
    if (log.detail && log.action !== 'Documents Migrated') {
      menu.push({
        value: 'SEE_DETAILS',
        label: translate('SEE_DETAILS'),
        action: () => {
          // Parse detail if it's a JSON string
          let parsedDetail = log.detail
          if (typeof log.detail === 'string') {
            try {
              parsedDetail = JSON.parse(log.detail)
            } catch (e) {
              // If parsing fails, use the string as-is
              console.warn('Failed to parse detail:', e)
            }
          }

          showModal(<ActivityDetailModal log={{ ...log, detail: parsedDetail } as any} />)
        }
      })
    }

    // Add "E-Signature" option if log has esignatureType
    if (log.esignatureType) {
      menu.push({
        value: 'ESIGNATURE',
        label: translate('ESIGNATURE'),
        action: () => {
          // Use esignatureComment directly from log object
          const esignatureComment = log.esignatureComment || ''
          showModal(<ActivityESignatureCommentModal esignatureComment={esignatureComment} />)
        }
      })
    }

    setOptions(menu)
  }, [log, translate, showModal])

  // Don't render if no menu options
  if (options.length === 0) {
    return null
  }

  return <ActionsMenu menuId={`action-menu-fsi-activity-${log.id}`} items={options} />
}

export default TableMenu
